import React from 'react';
import { XApi } from '../services.js';
import { useT } from '../i18n.jsx';
import { Dialog, DialogBody, DialogHeader } from '../ui/index.js';

const DEFAULT_THRESHOLD = '5';
const DEFAULT_TOP_UP = '20';
const ATTEMPT_STATES = new Set(['pending', 'canceled', 'succeeded', 'failed', 'partially_reversed', 'reversed']);
const amountInputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--line-2)',
  borderRadius: 6,
  background: 'var(--bg-2)',
  color: 'var(--fg-1)',
  font: 'inherit',
};

const validUsd = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 1 && amount <= 100 &&
    Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-7;
};
const displayUsd = (value) => {
  const amount = Number(value);
  return value != null && Number.isFinite(amount) ? amount.toFixed(2) : '—';
};

const readStatus = (value) => {
  if (!value || typeof value.available !== 'boolean' || typeof value.enabled !== 'boolean') {
    throw new Error('自动充值状态暂时无法确认，请稍后重试。');
  }
  return {
    available: value.available,
    enabled: value.enabled,
    suspended: Boolean(value.suspended),
    setupPending: Boolean(value.setupPending),
    pendingPayment: Boolean(value.pendingPayment),
    completedSetupSessionId: value.completedSetupSessionId || null,
    thresholdUsd: value.thresholdUsd,
    topUpUsd: value.topUpUsd,
    latestAttempt: value.latestAttempt &&
      ATTEMPT_STATES.has(value.latestAttempt.state) &&
      Number.isFinite(value.latestAttempt.amountUsd)
      ? value.latestAttempt : null,
    paymentMethod: value.paymentMethod || null,
  };
};

const checkoutUrl = (value, t) => {
  const url = value?.sessionUrl || value?.url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:' && parsed.hostname === 'checkout.stripe.com') return parsed.href;
  } catch { /* A malformed URL must not redirect the browser. */ }
  throw new Error(t('autoRecharge.setupUrlError'));
};

function SettingsContainer({ compact, title, onClose, children }) {
  if (!compact) return <>{children}</>;
  return <Dialog open onClose={onClose} maxWidth={560} maxHeight="88vh" responsive>
    <DialogHeader title={title} onClose={onClose} />
    <DialogBody padding={24}>{children}</DialogBody>
  </Dialog>;
}

export function AutoRechargeSettings({ ownerId, returnStatus, setupSessionId, compact = false, openRequest = false, onCloseRequest, onCheckoutRedirect = (url) => window.location.assign(url) }) {
  const { t } = useT();
  const [result, setResult] = React.useState({ ownerId: null, phase: 'loading', status: null });
  const [threshold, setThreshold] = React.useState(DEFAULT_THRESHOLD);
  const [topUp, setTopUp] = React.useState(DEFAULT_TOP_UP);
  const [consent, setConsent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [messageKind, setMessageKind] = React.useState('error');
  const [expanded, setExpanded] = React.useState(() => !compact || Boolean(returnStatus?.startsWith('auto-recharge-setup-')));
  const [manageMode, setManageMode] = React.useState('overview');
  const requestId = React.useRef(0);
  const ownerRef = React.useRef(ownerId);
  ownerRef.current = ownerId;

  const load = React.useCallback(async (owner, { keepDraft = false, preserveStatus = false, confirmSessionId = null, cancelSetup = false } = {}) => {
    const currentRequest = ++requestId.current;
    setResult((previous) => preserveStatus && previous.ownerId === owner
      ? { ...previous, phase: 'loading' }
      : { ownerId: owner, phase: 'loading', status: null });
    try {
      if (confirmSessionId === '') throw new Error('Missing Stripe setup session');
      const response = confirmSessionId
        ? await XApi.stripeService.confirmAutoRechargeSetup(confirmSessionId)
        : cancelSetup
          ? await XApi.stripeService.cancelAutoRechargeSetup()
          : await XApi.stripeService.getAutoRecharge();
      const status = readStatus(response);
      if (confirmSessionId && status.completedSetupSessionId !== confirmSessionId)
        throw new Error('Stripe setup session is not confirmed');
      if (currentRequest !== requestId.current || ownerRef.current !== owner) return null;
      setResult({ ownerId: owner, phase: 'ready', status });
      if (!keepDraft) {
        setThreshold(String(status.thresholdUsd ?? DEFAULT_THRESHOLD));
        setTopUp(String(status.topUpUsd ?? DEFAULT_TOP_UP));
        setConsent(false);
      }
      return status;
    } catch (error) {
      if (currentRequest !== requestId.current || ownerRef.current !== owner) return null;
      setResult({ ownerId: owner, phase: error?.status === 404 || error?.status === 501 ? 'unavailable' : 'error', status: null });
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (ownerId) {
      setMessage('');
      setMessageKind('error');
      setBusy(false);
      setExpanded(!compact || Boolean(returnStatus?.startsWith('auto-recharge-setup-')));
      setManageMode('overview');
      load(ownerId, {
        confirmSessionId: returnStatus === 'auto-recharge-setup-success' ? setupSessionId || '' : null,
        cancelSetup: returnStatus === 'auto-recharge-setup-cancel',
      });
    }
    return () => { requestId.current += 1; };
  }, [ownerId, load, compact, returnStatus, setupSessionId]);

  React.useEffect(() => {
    if (compact && returnStatus?.startsWith('auto-recharge-setup-')) setExpanded(true);
  }, [compact, returnStatus]);

  React.useEffect(() => {
    if (compact && openRequest) setExpanded(true);
  }, [compact, openRequest]);

  if (!ownerId) return null;
  const visible = result.ownerId === ownerId ? result : { phase: 'loading', status: null };
  const status = visible.status;
  const hasCard = Boolean(status?.paymentMethod?.last4);
  const setupVerified = returnStatus !== 'auto-recharge-setup-success' || (Boolean(setupSessionId) && status?.completedSetupSessionId === setupSessionId);
  const canEnable = Boolean(status?.available && !status.suspended && !status.setupPending && !status.pendingPayment && setupVerified);
  const retryReturn = () => load(ownerId, {
    confirmSessionId: returnStatus === 'auto-recharge-setup-success' ? setupSessionId || '' : null,
    cancelSetup: returnStatus === 'auto-recharge-setup-cancel',
  });
  const validAmounts = validUsd(threshold) && validUsd(topUp) && Number(topUp) > Number(threshold);

  const addCard = async () => {
    if (!status?.available || !validAmounts || !consent) return;
    setMessage('');
    setBusy(true);
    try {
      const url = checkoutUrl(await XApi.stripeService.createAutoRechargeSetupSession({
        thresholdUsd: Number(threshold),
        topUpUsd: Number(topUp),
        consentAccepted: true,
      }), t);
      if (ownerRef.current === ownerId) onCheckoutRedirect(url);
    } catch (error) {
      if (ownerRef.current === ownerId) {
        setMessageKind('error');
        setMessage(error?.message || t('autoRecharge.setupError'));
      }
    } finally {
      if (ownerRef.current === ownerId) setBusy(false);
    }
  };

  const save = async (enabled) => {
    if (enabled && (!canEnable || !hasCard || !validAmounts || !consent)) return;
    setBusy(true);
    setMessage('');
    try {
      await XApi.stripeService.saveAutoRecharge(enabled
        ? { enabled: true, thresholdUsd: Number(threshold), topUpUsd: Number(topUp), consentAccepted: true }
        : { enabled: false });
      if (ownerRef.current !== ownerId) return;
      const confirmed = await load(ownerId);
      if (confirmed?.enabled === enabled) {
        setMessage(enabled ? t('autoRecharge.saved') : t('autoRecharge.disabledMessage'));
        setMessageKind('success');
        setConsent(false);
        setManageMode('overview');
        if (!enabled && compact) setExpanded(false);
      } else if (ownerRef.current === ownerId) {
        setMessageKind('error');
        setMessage(t('autoRecharge.unconfirmed'));
      }
    } catch (error) {
      if (ownerRef.current === ownerId) {
        setMessageKind('error');
        setMessage(error?.message || t('autoRecharge.saveError'));
      }
    } finally {
      if (ownerRef.current === ownerId) setBusy(false);
    }
  };

  const resetManageMode = () => {
    setThreshold(String(status.thresholdUsd ?? DEFAULT_THRESHOLD));
    setTopUp(String(status.topUpUsd ?? DEFAULT_TOP_UP));
    setConsent(false);
    setMessage('');
    setManageMode('overview');
  };

  const closeSettings = () => {
    if (status) resetManageMode();
    setExpanded(false);
    if (openRequest) onCloseRequest?.();
  };

  const cardSummary = <div className="auto-recharge-payment-card">
    <div><div className="micro">{t('autoRecharge.card')}</div>
      <strong>{hasCard ? `${status?.paymentMethod?.brand || t('autoRecharge.card')} ···· ${status.paymentMethod.last4}` : t('autoRecharge.noCard')}</strong>
      {hasCard && status.paymentMethod.expMonth && status.paymentMethod.expYear && <small>{t('autoRecharge.expiry', { month: status.paymentMethod.expMonth, year: status.paymentMethod.expYear })}</small>}
    </div>
  </div>;

  const amountFields = <>
    <div className="auto-recharge-amount-grid">
      <label className="col gap-2" style={{ fontSize: 13 }}>
        <span>{t('autoRecharge.threshold')}</span>
        <input aria-label={t('autoRecharge.threshold')} type="number" min="1" max="100" step="0.01" value={threshold} style={amountInputStyle}
          onChange={(event) => { setThreshold(event.target.value); setConsent(false); }} disabled={busy} />
      </label>
      <label className="col gap-2" style={{ fontSize: 13 }}>
        <span>{t('autoRecharge.topUp')}</span>
        <input aria-label={t('autoRecharge.topUp')} type="number" min="1" max="100" step="0.01" value={topUp} style={amountInputStyle}
          onChange={(event) => { setTopUp(event.target.value); setConsent(false); }} disabled={busy} />
      </label>
    </div>
    {validAmounts
      ? <div className="auto-recharge-rule-preview">{t('autoRecharge.rulePreview', { threshold: displayUsd(threshold), amount: displayUsd(topUp) })}</div>
      : <p role="alert" className="auto-recharge-validation">{t('autoRecharge.invalidAmounts')}</p>}
  </>;

  const consentField = <label className="auto-recharge-consent">
    <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} disabled={busy} />
    <span>{t('autoRecharge.consent')}</span>
  </label>;

  return (
    <section className="card auto-recharge-settings" aria-label={t('autoRecharge.title')} style={{ marginTop: compact ? 0 : 28 }}>
      <div className="row between" style={{ gap: 16, alignItems: 'flex-start' }}>
        <div>
          {compact && <div className="billing-funding-icon" aria-hidden="true">↻</div>}
          <div className="micro">{t('autoRecharge.label')}</div>
          <h2 style={{ fontSize: 20, fontWeight: 500, margin: '8px 0' }}>{t(compact ? 'autoRecharge.cardTitle' : 'autoRecharge.title')}</h2>
          <p style={{ color: 'var(--fg-3)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {t(compact ? 'autoRecharge.cardDescription' : 'autoRecharge.description')}
          </p>
        </div>
        {status && <span className="tag" style={{ color: status.enabled ? 'var(--ok)' : 'var(--fg-3)', whiteSpace: 'nowrap' }}>
          {status.enabled ? t('autoRecharge.enabled') : t('autoRecharge.disabled')}
        </span>}
      </div>

      {visible.phase === 'loading' && <p role="status" style={{ fontSize: 13, color: 'var(--fg-3)' }}>{t('autoRecharge.loading')}</p>}
      {visible.phase === 'unavailable' && <p role="status" style={{ fontSize: 13, color: 'var(--fg-3)' }}>{t('autoRecharge.unavailable')}</p>}
      {status && !status.available && <p role="status" style={{ fontSize: 13, color: 'var(--fg-3)' }}>
        {t('autoRecharge.stripeUnavailable')}
      </p>}
      {status?.suspended && <p role="alert" className="auto-recharge-dialog-warning">{t('autoRecharge.suspendedHelp')}</p>}
      {status?.pendingPayment && <p role="status" className="auto-recharge-dialog-notice">{t('autoRecharge.pendingPaymentHelp')}</p>}
      {status?.setupPending && <p role="status" className="auto-recharge-dialog-notice">{t('autoRecharge.setupPending')}</p>}
      {status?.latestAttempt && <p role={status.latestAttempt.state === 'failed' ? 'alert' : 'status'} style={{ marginTop: 16, fontSize: 13, color: status.latestAttempt.state === 'failed' ? 'var(--err)' : 'var(--fg-3)' }}>
        {t('autoRecharge.attempt', { amount: status.latestAttempt.amountUsd.toFixed(2), state: t(`autoRecharge.attempt.${status.latestAttempt.state}`) })}
      </p>}
      {visible.phase === 'error' && <div style={{ marginTop: 16, fontSize: 13 }}>
        <p role="alert" style={{ color: 'var(--err)' }}>{t(returnStatus === 'auto-recharge-setup-success' ? 'autoRecharge.setupPending' : 'autoRecharge.loadError')}</p>
        <button type="button" className="btn btn-sm" onClick={retryReturn}>{t('autoRecharge.retry')}</button>
      </div>}

      {compact && status?.enabled && <div style={{ marginTop: 18, fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.6 }}>
        {t('autoRecharge.compactEnabled', { threshold: displayUsd(status.thresholdUsd), amount: displayUsd(status.topUpUsd) })}
      </div>}
      {compact && status && <div className="row gap-2" style={{ marginTop: 18, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-sm" aria-haspopup="dialog" onClick={() => { resetManageMode(); setExpanded(true); }}>
          {status.enabled ? t('autoRecharge.manage') : t('autoRecharge.expand')}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => load(ownerId)}>{t('autoRecharge.refresh')}</button>
      </div>}
      {compact && !expanded && message && <p role="status" className={messageKind === 'success' ? 'auto-recharge-message-success' : 'auto-recharge-message-error'}>{message}</p>}

      {compact && expanded && !status && <SettingsContainer compact title={t('autoRecharge.title')} onClose={closeSettings}>
        <p role="status" className="auto-recharge-dialog-notice">{t(visible.phase === 'loading' ? 'autoRecharge.loading' : visible.phase === 'unavailable' ? 'autoRecharge.unavailable' : returnStatus === 'auto-recharge-setup-success' ? 'autoRecharge.setupPending' : 'autoRecharge.loadError')}</p>
        {visible.phase === 'error' && <button type="button" className="btn btn-sm" onClick={retryReturn}>{t('autoRecharge.retry')}</button>}
      </SettingsContainer>}
      {status && expanded && <SettingsContainer compact={compact} title={t('autoRecharge.title')} onClose={closeSettings}>
        <div className="auto-recharge-dialog">
          {returnStatus === 'auto-recharge-setup-cancel' && !status.enabled && <p role="status" className="auto-recharge-dialog-notice">{t('autoRecharge.setupCancel')}</p>}
          {returnStatus === 'auto-recharge-setup-success' && <p role="status" className="auto-recharge-dialog-notice">
            {setupVerified && hasCard ? t('autoRecharge.setupReady') : t('autoRecharge.setupPending')}
          </p>}
          {visible.phase === 'loading' && <p role="status" className="auto-recharge-dialog-notice">{t('autoRecharge.loading')}</p>}
          {compact && status.latestAttempt?.state === 'failed' && <p role="alert" className="auto-recharge-dialog-warning">{t('autoRecharge.failureHelp')}</p>}
          {hasCard ? <>
            {manageMode === 'overview' ? <>
              {returnStatus !== 'auto-recharge-setup-success' && <p className="auto-recharge-dialog-intro">{t(status.enabled ? 'autoRecharge.manageIntro' : 'autoRecharge.readyIntro')}</p>}
              <div className="auto-recharge-current-rule">
                <span>{t('autoRecharge.currentRule')}</span>
                <strong>{t('autoRecharge.rulePreview', { threshold: displayUsd(status.thresholdUsd), amount: displayUsd(status.topUpUsd) })}</strong>
              </div>
              {cardSummary}
              {!status.enabled && <>
                <label className="auto-recharge-consent auto-recharge-manage-consent">
                  <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} disabled={busy} />
                  <span>{t('autoRecharge.activateConsent', { threshold: displayUsd(threshold), amount: displayUsd(topUp) })}</span>
                </label>
                <p className="auto-recharge-authorization-limits">{t('autoRecharge.authorizationLimits')}</p>
                <details className="auto-recharge-terms"><summary>{t('autoRecharge.fullTerms')}</summary><p>{t('autoRecharge.consent')}</p></details>
              </>}
              <div className="auto-recharge-manage-actions">
                {status.enabled
                  ? <button type="button" className="btn btn-primary" disabled={busy || !status.available} onClick={() => setManageMode('rule')}>{t('autoRecharge.editRule')}</button>
                  : <button type="button" className="btn btn-primary" disabled={busy || !canEnable || !validAmounts || !consent} onClick={() => save(true)}>{busy ? t('autoRecharge.busy') : t('autoRecharge.enable')}</button>}
                {!status.enabled && <button type="button" className="btn" disabled={busy || !status.available} onClick={() => { setConsent(false); setManageMode('rule'); }}>{t('autoRecharge.editRule')}</button>}
                <button type="button" className="btn" disabled={busy || !status.available} onClick={() => setManageMode('card')}>{t('autoRecharge.changeCard')}</button>
                {status.enabled && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => save(false)}>{t('autoRecharge.disable')}</button>}
              </div>
            </> : <div className="auto-recharge-manage-form">
              <button type="button" className="auto-recharge-back" disabled={busy} onClick={resetManageMode}>← {t('autoRecharge.backToOverview')}</button>
              <h3 className="auto-recharge-manage-title">{t(manageMode === 'rule' ? 'autoRecharge.editRule' : 'autoRecharge.changeCard')}</h3>
              <p className="auto-recharge-dialog-intro">{t(manageMode === 'rule' ? 'autoRecharge.editRuleHint' : status.enabled ? 'autoRecharge.changeCardWarning' : 'autoRecharge.changeCardInactiveHint')}</p>
              {manageMode === 'rule' ? amountFields : cardSummary}
              <label className="auto-recharge-consent">
                <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} disabled={busy} />
                <span>{t(manageMode === 'rule' ? 'autoRecharge.editRuleConsent' : 'autoRecharge.changeCardConsent', {
                  threshold: displayUsd(threshold), amount: displayUsd(topUp),
                })}</span>
              </label>
              {manageMode === 'rule' && <p className="auto-recharge-authorization-limits">{t('autoRecharge.authorizationLimits')}</p>}
              <details className="auto-recharge-terms"><summary>{t('autoRecharge.fullTerms')}</summary><p>{t('autoRecharge.consent')}</p></details>
              <div className="auto-recharge-dialog-actions">
                <button type="button" className="btn btn-primary" disabled={busy || (manageMode === 'rule' ? !canEnable : !status.available) || !validAmounts || !consent} onClick={manageMode === 'rule' ? () => save(true) : addCard}>
                  {busy ? t('autoRecharge.busy') : t(manageMode === 'rule' ? status.enabled ? 'autoRecharge.save' : 'autoRecharge.enable' : 'autoRecharge.changeCardNext')}
                </button>
              </div>
            </div>}
          </> : <>
            <p className="auto-recharge-dialog-intro">{t('autoRecharge.flowIntro')}</p>
            <section className="auto-recharge-step" aria-label={t('autoRecharge.ruleStep')}>
              <div className="auto-recharge-step-heading"><span>1</span><div><h3>{t('autoRecharge.ruleStep')}</h3><p>{t('autoRecharge.ruleHint')}</p></div></div>
              {amountFields}
            </section>
            <section className="auto-recharge-step" aria-label={t('autoRecharge.cardStep')}>
              <div className="auto-recharge-step-heading"><span>2</span><div><h3>{t('autoRecharge.cardStep')}</h3><p>{t('autoRecharge.cardStepHint')}</p></div></div>
              {cardSummary}
              {hasCard && !consent && <p className="auto-recharge-step-note">{t('autoRecharge.changeCardHint')}</p>}
              {!hasCard && <p className="auto-recharge-step-note">{t('autoRecharge.setupNext')}</p>}
            </section>
            <section className="auto-recharge-step" aria-label={t('autoRecharge.consentStep')}>
              <div className="auto-recharge-step-heading"><span>3</span><div><h3>{t('autoRecharge.consentStep')}</h3></div></div>
              {consentField}
            </section>
            <div className="auto-recharge-dialog-actions">
              <button type="button" className="btn btn-primary" disabled={busy || (hasCard ? !canEnable : !status.available) || !validAmounts || !consent} onClick={hasCard ? () => save(true) : addCard}>
                {busy ? t('autoRecharge.busy') : !hasCard ? t('autoRecharge.addCardNext') : t('autoRecharge.enable')}
              </button>
              {hasCard && <button type="button" className="btn" disabled={busy || !status.available || !validAmounts || !consent} onClick={addCard}>{t('autoRecharge.changeCard')}</button>}
              {returnStatus === 'auto-recharge-setup-success' && !hasCard && <button type="button" className="btn" disabled={busy || visible.phase === 'loading'} onClick={() => load(ownerId, { keepDraft: true, preserveStatus: true })}>{t('autoRecharge.refreshCard')}</button>}
            </div>
          </>}
          {message && <p role="status" className={messageKind === 'success' ? 'auto-recharge-message-success' : 'auto-recharge-message-error'}>{message}</p>}
        </div>
      </SettingsContainer>}
    </section>
  );
}
